<script setup lang="ts">
import FormField from '@/components/input/FormField.vue'
import { SelectOption } from '@/components/input/SelectField.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import {
  EbomPartPickerDataQuery,
  EbomPartPickerDataQueryVariables,
  EbomPartPickerPathNamesQuery,
  EbomPartPickerPathNamesQueryVariables,
  EbomPartSearchQuery,
} from '@/generated/graphql'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, ref, watch } from 'vue'
import { debounce } from 'lodash'

const props = defineProps<{
  ebomId: string
  modelValue?: EbomPartPickerOption
  label?: string
  stepId?: string
  graphNodeStepId?: string
}>()
defineEmits<{
  'update:modelValue': [value: EbomPartPickerOption]
}>()

export type EbomPartPickerOption = EbomPartPickerDataQuery['article']['ebomParts'][0]

const searchString = ref('')
const debouncedSearchString = ref('')
const debouncer = debounce(() => {
  debouncedSearchString.value = searchString.value
}, 300)
watch(searchString, debouncer)

const query = computed<EbomPartSearchQuery>(() => ({
  ebomId: props.ebomId,
  searchString: debouncedSearchString.value,
  contextStepId: props.stepId,
  contextGraphNodeStepId: props.graphNodeStepId,
}))

const fetchQuery = useQuery<EbomPartPickerDataQuery, EbomPartPickerDataQueryVariables>(
  gql`
    query EbomPartPickerData($query: EbomPartSearchQuery!) {
      article {
        ebomParts(query: $query) {
          id
          articleNumber
          revision
          name
          unit
          milliQuantity
          articlePath {
            articleNumbers
          }
          stepAllocations {
            id
            milliQuantity
          }
          article {
            id
          }
        }
      }
    }
  `,
  () => ({ query: query.value }),
)
const items = computed<SelectOption<EbomPartPickerOption>[]>(
  () =>
    fetchQuery.result.value?.article.ebomParts
      .filter((p) => !!p.article)
      .map((p) => ({
        title: 'ignored',
        value: p,
      })) || [],
)

const pathNamesQuery = useQuery<
  EbomPartPickerPathNamesQuery,
  EbomPartPickerPathNamesQueryVariables
>(
  gql`
    query EbomPartPickerPathNames($ebomId: ID!) {
      article {
        ebom(id: $ebomId) {
          id
          ebomParts {
            id
            articleNumber
            name
          }
        }
      }
    }
  `,
  () => ({ ebomId: props.ebomId }),
)
const nameByArticleNumber = computed(() => {
  const names = new Map<string, string>()
  pathNamesQuery.result.value?.article.ebom.ebomParts.forEach((p) => {
    if (p.name && !names.has(p.articleNumber)) {
      names.set(p.articleNumber, p.name)
    }
  })
  return names
})
const pathLabel = (option: EbomPartPickerOption) =>
  option.articlePath.articleNumbers
    .slice(0, -1)
    .map((articleNumber) => nameByArticleNumber.value.get(articleNumber) || articleNumber)
    .join(' > ')

const netMilliQuantity = (option: EbomPartPickerOption) =>
  option.stepAllocations.reduce((agg, a) => agg + a.milliQuantity, 0) - option.milliQuantity
</script>

<template>
  <form-field :label="props.label">
    <v-autocomplete
      hide-details
      v-bind="$attrs"
      :model-value="props.modelValue"
      :items="items"
      auto-select-first
      no-filter
      :menu-props="{ width: 640 }"
      :loading="fetchQuery.loading.value"
      no-data-text="No parts found"
      @update:search="(v) => (searchString = v)"
      @update:model-value="(v) => $emit('update:modelValue', v)"
    >
      <template #selection="{ item }: { item: SelectOption<EbomPartPickerOption> }">
        <v-tooltip
          location="top"
          :text="`${item.value.articleNumber}/${item.value.revision} ${item.value.name}`"
        >
          <template #activator="{ props: activatorProps }">
            <span v-bind="activatorProps" class="text-truncate" style="max-width: 100%">
              {{ item.value.articleNumber }}/{{ item.value.revision }} {{ item.value.name }}
            </span>
          </template>
        </v-tooltip>
      </template>
      <template
        #item="{
          item,
          props: itemProps,
        }: {
          item: SelectOption<EbomPartPickerOption>
          props: Record<string, unknown>
        }"
      >
        <v-list-item
          v-bind="itemProps"
          :title="`${item.value.articleNumber}/${item.value.revision} ${item.value.name}`"
          :subtitle="pathLabel(item.value)"
          class="part-option"
        >
          <template #append>
            <v-chip size="small" :color="netMilliQuantity(item.value) == 0 ? 'success' : 'error'">
              <article-quantity
                :milli-quantity="netMilliQuantity(item.value)"
                :unit="item.value.unit"
                unit-abbreviated
              />
            </v-chip>
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </form-field>
</template>

<style scoped lang="scss">
.part-option :deep(.v-list-item-title),
.part-option :deep(.v-list-item-subtitle) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
